import { useEffect, useState, type FormEvent } from 'react';
import { Mail, Globe, Loader2, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/services/api';
import { Modal } from '@/components/ui/Modal';
import { Card } from '@/components/ui/Card';

/**
 * ConnectedAccountsCard — per-applicant external account hookup. Used inside
 * Settings → Advanced. Hidden from the main app flow per Peter's "extremely
 * concise" UI direction.
 *
 * Today: Gmail App Password (works without Google Cloud). Tomorrow: Gmail
 * OAuth (when GOOGLE_CLIENT_ID is set) + browser session for LinkedIn /
 * "Apply with Google" buttons (when BROWSER_SESSION_SETUP_ENABLED is set).
 */

interface AccountStatus {
  applicant_id: string;
  gmail: { address: string; kind: 'app_password' | 'oauth' } | null;
  browser_session: { last_login_at: string } | null;
  capabilities: {
    google_oauth_configured: boolean;
    browser_session_supported: boolean;
  };
}

export function ConnectedAccountsCard({ applicantId }: { applicantId: string }) {
  const [status, setStatus] = useState<AccountStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [pwModal, setPwModal] = useState(false);
  const [browserModal, setBrowserModal] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.get<AccountStatus>(`/accounts/status?applicant_id=${applicantId}`);
      setStatus(r);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantId]);

  async function disconnectGmail() {
    if (!window.confirm('Disconnect Gmail? Verification codes for this user will stop arriving.')) return;
    try {
      await api.delete(`/accounts/gmail?applicant_id=${applicantId}`);
      toast.success('Disconnected');
      void refresh();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'failed');
    }
  }

  return (
    <>
      <Card title={`Connected accounts — ${applicantId}`}>
        {loading || !status ? (
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {/* Gmail row */}
            <li className="flex items-center justify-between gap-3 py-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Gmail</span>
                {status.gmail ? (
                  <span className="ml-2 text-xs text-gray-500">
                    <Check className="mr-0.5 inline h-3 w-3 text-green-600" />
                    {status.gmail.address}
                    <code className="ml-1 rounded bg-gray-100 px-1 text-[10px] dark:bg-gray-800">
                      {status.gmail.kind}
                    </code>
                  </span>
                ) : (
                  <span className="ml-2 text-xs text-gray-400">not connected</span>
                )}
              </div>
              <div className="flex gap-1">
                {status.gmail && (
                  <button onClick={disconnectGmail} className="btn-secondary text-xs">
                    Disconnect
                  </button>
                )}
                <button
                  onClick={() => setPwModal(true)}
                  className="btn-secondary text-xs"
                  title="Connect Gmail via app password (works without Google Cloud setup)"
                >
                  Use app password
                </button>
                <button
                  className="btn-primary text-xs"
                  disabled={!status.capabilities.google_oauth_configured}
                  title={
                    status.capabilities.google_oauth_configured
                      ? 'Connect Gmail via Google OAuth (one click)'
                      : 'OAuth not configured — set GOOGLE_CLIENT_ID in /opt/jobapplier/.env first'
                  }
                  onClick={() => {
                    window.location.href = `/api/accounts/google/start?applicant_id=${applicantId}`;
                  }}
                >
                  Connect with Google
                </button>
              </div>
            </li>

            {/* Browser session row */}
            <li className="flex items-center justify-between gap-3 py-2">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Browser session</span>
                {status.browser_session ? (
                  <span className="ml-2 text-xs text-gray-500">
                    <Check className="mr-0.5 inline h-3 w-3 text-green-600" />
                    LinkedIn cookies stored
                  </span>
                ) : (
                  <span className="ml-2 text-xs text-gray-400">not connected</span>
                )}
              </div>
              <button
                className="btn-primary text-xs"
                onClick={() => setBrowserModal(true)}
                title="Open a live browser on the server — log in to LinkedIn / Google once; cookies persist"
              >
                Log in once
              </button>
            </li>
          </ul>
        )}
        <p className="mt-3 text-xs text-gray-500">
          Gmail is used to read application-verification codes and send referral emails.
          The browser session is for ATS that require an active login
          (LinkedIn Easy Apply, "Apply with Google", etc.).
        </p>
      </Card>

      <GmailAppPwModal
        open={pwModal}
        onClose={() => setPwModal(false)}
        applicantId={applicantId}
        onConnected={() => {
          setPwModal(false);
          void refresh();
        }}
      />

      <BrowserSessionModal
        open={browserModal}
        onClose={() => setBrowserModal(false)}
        applicantId={applicantId}
      />
    </>
  );
}

function BrowserSessionModal({
  open,
  onClose,
  applicantId,
}: {
  open: boolean;
  onClose: () => void;
  applicantId: string;
}) {
  const [starting, setStarting] = useState(false);
  const [vncUrl, setVncUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setStarting(true);
      setError(null);
      try {
        const r = (await api.post('/browser/start', { applicant_id: applicantId })) as {
          active: boolean;
          ws_path?: string;
          novnc_path?: string;
        };
        if (cancelled) return;
        if (r.active && r.ws_path) {
          // noVNC client expects: host, port, path. We build a URL that
          // points the client at our WebSocket proxy.
          const host = window.location.hostname;
          const isHttps = window.location.protocol === 'https:';
          const port = isHttps ? '443' : '80';
          const path = r.ws_path.replace(/^\//, '');
          const novnc = r.novnc_path ?? '/vnc-static/vnc_lite.html';
          setVncUrl(
            `${novnc}?host=${host}&port=${port}&path=${path}&encrypt=${isHttps ? '1' : '0'}&autoconnect=true&resize=scale`,
          );
        } else {
          setError('session_did_not_start');
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, applicantId]);

  async function closeAndStop() {
    setVncUrl(null);
    try {
      await api.post('/browser/stop', { applicant_id: applicantId });
    } catch {
      /* ignore */
    }
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={closeAndStop}
      title={`Browser session — ${applicantId}`}
      size="xl"
      footer={
        <button onClick={closeAndStop} className="btn-secondary">
          Done — close session
        </button>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-gray-600 dark:text-gray-300">
          A live browser is running on the server. Log in to <strong>LinkedIn</strong>,{' '}
          <strong>Google</strong>, or any other site the bot needs to act as you. Close this
          window when finished — cookies stay on the server so future applies work.
        </p>
        {starting && (
          <div className="flex h-72 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Starting browser…</span>
          </div>
        )}
        {error && (
          <p className="flex items-center gap-1 text-xs text-red-600">
            <AlertCircle className="h-3.5 w-3.5" />
            {error}
          </p>
        )}
        {vncUrl && (
          <iframe
            src={vncUrl}
            title="Browser session"
            className="h-[600px] w-full rounded-md border border-gray-200 dark:border-gray-700"
            allow="clipboard-read; clipboard-write"
          />
        )}
      </div>
    </Modal>
  );
}

function GmailAppPwModal({
  open,
  onClose,
  applicantId,
  onConnected,
}: {
  open: boolean;
  onClose: () => void;
  applicantId: string;
  onConnected: () => void;
}) {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post('/accounts/gmail-app-password', {
        applicant_id: applicantId,
        email,
        app_password: pw,
      });
      toast.success(`Gmail connected: ${email}`);
      setEmail('');
      setPw('');
      onConnected();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Connect Gmail (app password)"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button onClick={submit} disabled={saving || !email || !pw} className="btn-primary">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <p className="text-xs text-gray-500">
          One-time setup: at{' '}
          <a
            href="https://myaccount.google.com/apppasswords"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline"
          >
            myaccount.google.com/apppasswords
          </a>{' '}
          create a 16-character app password and paste it below. We store it
          encrypted at rest on the server; never logged.
        </p>
        <label className="block text-xs font-medium text-gray-700">
          Gmail address
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input mt-1"
            placeholder="you@gmail.com"
            required
          />
        </label>
        <label className="block text-xs font-medium text-gray-700">
          App password (16 chars)
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="input mt-1 font-mono"
            placeholder="xxxx xxxx xxxx xxxx"
            required
          />
        </label>
        {error && (
          <p className="flex items-center gap-1 text-xs text-red-600">
            <AlertCircle className="h-3.5 w-3.5" />
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
